import { useCallback } from 'react';
import * as ViewModel from '@/core/presenter/viewModel';
import clsx from 'clsx';
import { useStores } from '@/stores';
import { removeSignPrefix, removeSymbol, truncateString } from '@/utils';
import { useNavigate } from 'react-router-dom';
import { RouterPath } from '@/routes/RouterPath';
import { tryFetchBalance } from '@/core/presenter/controller/HomeController';

interface IProps {
  trxTransaction: ViewModel.IResponseActivity;
  onCancel: () => void;
}

const TrxTransactionPopup = ({ trxTransaction, onCancel }: IProps) => {
  const { userStore } = useStores();
  const navigate = useNavigate();

  const fromAddress = truncateString(trxTransaction.from ?? '');
  const toAddress = truncateString(trxTransaction.to ?? '');
  const isReceive = trxTransaction.type === '받기';
  const isSend = trxTransaction.type === '보내기';
  const fromMemo = trxTransaction.from_memo || '';
  const toMemo = trxTransaction.to_memo || '';

  const onClickTrxAddressCopyBtn = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
  }, []);

  const onClickCopyBtn = useCallback(() => {
    navigator.clipboard.writeText(trxTransaction.txID);
  }, [trxTransaction.txID]);

  const onClickTrxExplorerBtn = useCallback(() => {
    const baseUrl = import.meta.env.VITE_SOLSCAN_EXPLORER_URL || 'https://solscan.io';
    const cluster = import.meta.env.VITE_SOLSCAN_CLUSTER || 'devnet';
    window.open(`${baseUrl}/tx/${trxTransaction.txID}?cluster=${cluster}`, '_blank');
  }, [trxTransaction.txID]);

  const onClickResendBtn = useCallback(async () => {
    if (userStore.user && userStore.user.address) {
      const isSuccess = await tryFetchBalance(userStore.user.address);
      if (isSuccess) {
        navigate(RouterPath.HOME, {
          state: {
            viewType: ViewModel.HomeViewType.SEND,
            sendData: {
              address: isSend ? trxTransaction.to : trxTransaction.from,
            },
          },
        });
      }
    }

    onCancel();
  }, [userStore.user, onCancel, navigate, isSend, trxTransaction.to, trxTransaction.from]);

  return (
    <div className="modal-overlay" id="trxDetailModal">
      <div className="modal-content modal-detail modal-medium">
        <button type="button" className="modal-close" onClick={onCancel}>
          <img src="./images/close-icon.png" alt="닫기" className="close-icon" />
        </button>

        <div className="modal-header">
          <h2 className="detail-modal-title heading-4 font-bold">{trxTransaction.type} 상세내역</h2>
        </div>

        <div className="modal-body">
          {/* 상태와 링크 */}
          <div className="detail-top-section">
            <div className="detail-top-row">
              <span className="detail-status-text text-base font-medium">상태</span>
              <button type="button" className="detail-action-link text-sm font-normal" onClick={onClickTrxExplorerBtn}>
                블록탐색기에서 보기
                <img src="./images/search-icon.png" alt="블록탐색기에서 보기" className="search-icon" />
              </button>
            </div>

            <div className="detail-top-row">
              <div
                className={clsx('detail-badge', {
                  fail: trxTransaction.status === ViewModel.TransactionStatus.FAIL,
                })}
              >
                <span className="text-xs font-semibold">{trxTransaction.status}</span>
              </div>
              <button type="button" className="detail-action-link text-sm font-normal" onClick={onClickCopyBtn}>
                트랜잭션 ID 복사
                <img src="./images/copy-icon.png" alt="복사" className="copy-icon-small" />
              </button>
            </div>
          </div>

          {/* 보낸 사람 / 받은 사람 */}
          <div className="detail-people-section">
            <div className="people-header">
              <h3 className="people-title text-base font-bold">보낸 사람</h3>
              <h3 className="people-title text-base font-bold">받은 사람</h3>
            </div>

            <div className="people-cards">
              <div className="people-cards-row">
                {/* 보낸 사람 */}
                <div className="people-card">
                  <div className="people-address-row">
                    <span className={clsx('people-address text-sm font-medium', { 'back-round-gray': isSend })}>
                      {fromAddress}
                    </span>
                    {isReceive && (
                      <button
                        type="button"
                        className="people-copy-btn"
                        onClick={() => onClickTrxAddressCopyBtn(trxTransaction.from ?? '')}
                      >
                        <img src="./images/copy-icon.png" alt="복사" className="copy-icon-small" />
                      </button>
                    )}
                  </div>
                  <p className="people-name text-sm font-normal">{isSend ? '나' : fromMemo}</p>
                </div>

                {/* 화살표 */}
                <div className="people-arrow">
                  <img src="./images/send-arrow-right.png" alt="화살표" className="send-arrow-icon" />
                </div>

                {/* 받은 사람 */}
                <div className="people-card right">
                  <div className="people-address-row">
                    <span className={clsx('people-address text-sm font-medium', { 'back-round-gray': isReceive })}>
                      {toAddress}
                    </span>
                    {isSend && (
                      <button
                        type="button"
                        className="people-copy-btn"
                        onClick={() => onClickTrxAddressCopyBtn(trxTransaction.to ?? '')}
                      >
                        <img src="./images/copy-icon.png" alt="복사" className="copy-icon-small" />
                      </button>
                    )}
                  </div>
                  <p className="people-name text-sm font-normal">{isReceive ? '나' : toMemo}</p>
                </div>
              </div>

              {/* 보내기 버튼 */}
              <button type="button" className="send-to-person-btn text-sm font-semibold" onClick={onClickResendBtn}>
                {isReceive ? `${fromMemo || fromAddress}에게 보내기` : `${toMemo || toAddress}에게 보내기`}
              </button>
            </div>
          </div>

          {/* 트랜잭션 */}
          <h3 className="transaction-section-title text-base font-bold">트랜잭션</h3>
          <div className="detail-transaction-section">
            <div className="transaction-item">
              <span className="transaction-amount-label text-base font-medium">금액</span>
              <div className="transaction-amount-values">
                <span
                  className={clsx('transaction-amount-value text-lg font-semibold', {
                    'transaction-amount-receive': isReceive,
                    'transaction-amount-send': isSend,
                  })}
                >
                  {removeSymbol(removeSignPrefix(trxTransaction.amount))}
                </span>
                <span className="transaction-amount-krw text-sm font-normal">{trxTransaction.krwAmount}</span>
              </div>
            </div>

            {isSend && (
              <div className="transaction-item">
                <span className="transaction-fee-label text-base font-medium">수수료</span>
                <span className="transaction-fee-value text-sm font-normal">
                  {trxTransaction.fee} {trxTransaction.coinType}
                </span>
              </div>
            )}
          </div>

          {/* 확인 버튼 */}
          <button type="button" className="modal-button text-lg font-semibold" onClick={onCancel}>
            확인
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrxTransactionPopup;
